import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';
import {
  ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import {
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineExclamation,
  HiOutlineOfficeBuilding,
  HiOutlineRefresh,
  HiOutlineCash,
  HiOutlineX,
} from 'react-icons/hi';
import { ReportGuide } from '../../components/SuperAdmin/ReportGuide';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const TABS = [
  { key: 'upcoming', label: 'Upcoming', icon: HiOutlineCalendar, color: 'text-blue-500', activeBg: 'bg-blue-500' },
  { key: 'pending', label: 'Pending', icon: HiOutlineClock, color: 'text-amber-500', activeBg: 'bg-amber-500' },
  { key: 'completed', label: 'Completed', icon: HiOutlineCheckCircle, color: 'text-green-500', activeBg: 'bg-green-500' },
  { key: 'missed', label: 'Missed', icon: HiOutlineExclamation, color: 'text-red-500', activeBg: 'bg-red-500' },
];

const EMPTY_RENEWAL_FILTERS = { search: '', plan: '', cycle: '', status: '', owner: '', from: '', to: '' };

// Normalize org-based rows (upcoming/missed) and task rows (pending/completed)
// into one shape for the table
const normalizeRows = (rows, tab) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    if (tab === 'upcoming' || tab === 'missed') {
      return {
        _id: r._id,
        orgId: r._id,
        organizationName: r.organizationName,
        organizationEmail: r.organizationEmail,
        organizationPhone: r.organizationPhone,
        city: r.city,
        state: r.state,
        planName: r.planName,
        billingCycle: r.billingCycle,
        isTrial: r.isTrial,
        subscriptionStartsAt: r.subscriptionStartsAt,
        ownerName: r.adminName,
        ownerEmail: r.adminEmail,
        dueDate: r.dueDate,
        daysLeft: r.daysLeft,
        overdueDays: r.overdueDays,
        status: tab === 'missed' ? 'missed' : r.orgStatus,
      };
    }
    const org = r.organizationId && typeof r.organizationId === 'object' ? r.organizationId : null;
    const assignee = r.assignedTo && typeof r.assignedTo === 'object' ? r.assignedTo : null;
    const assigneeName = assignee
      ? [assignee.profile?.firstName || assignee.firstName, assignee.profile?.lastName || assignee.lastName]
          .filter(Boolean)
          .join(' ')
      : null;
    return {
      _id: r._id,
      orgId: org?._id || null,
      organizationName: org?.name || '—',
      organizationEmail: org?.email || null,
      organizationPhone: org?.phone || null,
      ownerName: assigneeName || null,
      ownerEmail: assignee?.email || null,
      dueDate: r.dueDate,
      daysLeft: null,
      overdueDays: null,
      status: r.status,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    };
  });
};

const RenewalPerformanceReport = () => {
  const [activeTab, setActiveTab] = useState('upcoming');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['renewal-performance-report'],
    queryFn: superAdminAPI.getRenewalReport,
    refetchInterval: 120000,
  });

  // ── Renew-subscription action (from this report, no page hop) ──
  const queryClient = useQueryClient();
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewCycle, setRenewCycle] = useState('');

  const renewMutation = useMutation({
    mutationFn: ({ orgId, payload }) => superAdminAPI.renewSubscription(orgId, payload),
    onSuccess: (res) => {
      const newEnd = res?.data?.data?.subscriptionEndsAt;
      toast.success(`Subscription renewed${newEnd ? ` — now ends ${formatDate(newEnd)}` : ''}`);
      setRenewTarget(null);
      queryClient.invalidateQueries({ queryKey: ['renewal-performance-report'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to renew subscription'),
  });

  const handleRenew = () => {
    if (!renewTarget) return;
    const payload = { months: renewMonths };
    if (renewCycle) payload.billingCycle = renewCycle;
    renewMutation.mutate({ orgId: renewTarget.orgId, payload });
  };

  // Axios → res.data (server body) → body.data (report)
  const report = data?.data?.data || {};
  const allRows = normalizeRows(report[activeTab], activeTab);

  const statValue = (key) => report.stats?.[key] ?? 0;

  const isOrgTab = activeTab === 'upcoming' || activeTab === 'missed';

  const [filters, setFilters] = useState(EMPTY_RENEWAL_FILTERS);
  const filterActive = Object.keys(EMPTY_RENEWAL_FILTERS).some((k) => filters[k]);
  const rows = allRows.filter((r) =>
    matchesSearch(filters.search, r.organizationName, r.organizationEmail, r.organizationPhone, r.ownerName, r.ownerEmail, r.city)
    && (!filters.plan || r.planName === filters.plan)
    && (!filters.cycle || (filters.cycle === 'trial' ? r.isTrial : !r.isTrial && r.billingCycle === filters.cycle))
    && (!filters.status || r.status === filters.status)
    && (!filters.owner || (r.ownerName || 'Unassigned') === filters.owner)
    && inDateRange(r.dueDate, filters.from, filters.to));
  const switchTab = (key) => { setActiveTab(key); setFilters(EMPTY_RENEWAL_FILTERS); };

  const exportColumns = isOrgTab
    ? [
      { header: 'Organization', value: (r) => r.organizationName },
      { header: 'Email', value: (r) => r.organizationEmail },
      { header: 'Phone', value: (r) => r.organizationPhone },
      { header: 'City', value: (r) => r.city },
      { header: 'State', value: (r) => r.state },
      { header: 'Plan', value: (r) => r.planName },
      { header: 'Billing', value: (r) => (r.isTrial ? 'Trial' : r.billingCycle) },
      { header: 'Admin', value: (r) => r.ownerName },
      { header: 'Admin Email', value: (r) => r.ownerEmail },
      { header: 'Subscription Start', value: (r) => formatExportDate(r.subscriptionStartsAt, false) },
      { header: 'Expiry Date', value: (r) => formatExportDate(r.dueDate, false) },
      activeTab === 'missed'
        ? { header: 'Overdue (days)', value: (r) => r.overdueDays }
        : { header: 'Due In (days)', value: (r) => r.daysLeft },
      { header: 'Org Status', value: (r) => r.status },
    ]
    : [
      { header: 'Organization', value: (r) => r.organizationName },
      { header: 'Email', value: (r) => r.organizationEmail },
      { header: 'Phone', value: (r) => r.organizationPhone },
      { header: 'Assigned To', value: (r) => r.ownerName },
      { header: 'Assignee Email', value: (r) => r.ownerEmail },
      { header: 'Due Date', value: (r) => formatExportDate(r.dueDate, false) },
      { header: 'Task Status', value: (r) => r.status },
      { header: activeTab === 'completed' ? 'Completed' : 'Created', value: (r) => formatExportDate(r.updatedAt || r.createdAt) },
    ];
  const columns =
    activeTab === 'upcoming' || activeTab === 'missed'
      ? ['Organization', 'Phone Number', 'Plan', 'Admin', 'Expiry Date', activeTab === 'missed' ? 'Overdue' : 'Due In', 'Org Status', 'Action']
      : ['Organization', 'Phone Number', 'Assigned To', 'Due Date', 'Task Status', activeTab === 'completed' ? 'Completed' : 'Created', 'Action'];

  // Shared cells: org contact (name + email) and its phone number
  const orgCell = (row) => (
    <div className="flex items-start gap-2">
      <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-medium text-gray-900">{row.organizationName}</p>
        {row.organizationEmail && (
          <p className="text-xs text-gray-400 truncate max-w-[200px]" title={row.organizationEmail}>
            {row.organizationEmail}
          </p>
        )}
      </div>
    </div>
  );
  const phoneCell = (row) =>
    row.organizationPhone ? (
      <span className="text-gray-600 whitespace-nowrap">{row.organizationPhone}</span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  const ownerCell = (row) =>
    row.ownerName ? (
      <div>
        <p className="text-gray-700">{row.ownerName}</p>
        {row.ownerEmail && (
          <p className="text-xs text-gray-400 truncate max-w-[180px]" title={row.ownerEmail}>
            {row.ownerEmail}
          </p>
        )}
      </div>
    ) : (
      <span className="text-gray-400">—</span>
    );

  const renderCell = (row, colIndex) => {
    if (isOrgTab) {
      switch (colIndex) {
        case 0:
          return orgCell(row);
        case 1:
          return phoneCell(row);
        case 2:
          return (
            <div>
              <p className="text-gray-600">{row.planName || '—'}</p>
              <p className="text-xs text-gray-400">
                {row.isTrial ? 'Trial' : (row.billingCycle ? `${row.billingCycle} billing` : null) || '—'}
              </p>
            </div>
          );
        case 3:
          return ownerCell(row);
        case 4:
          return (
            <div>
              <p className="text-gray-600">{formatDate(row.dueDate)}</p>
              {row.subscriptionStartsAt && (
                <p className="text-xs text-gray-400">since {formatDate(row.subscriptionStartsAt)}</p>
              )}
            </div>
          );
        case 5:
          if (activeTab === 'missed') {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                {row.overdueDays} day{row.overdueDays !== 1 ? 's' : ''} overdue
              </span>
            );
          }
          {
            const urgent = row.daysLeft <= 3;
            return (
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                urgent ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
              }`}>
                in {row.daysLeft} day{row.daysLeft !== 1 ? 's' : ''}
              </span>
            );
          }
        case 6:
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
              row.status === 'missed' ? 'bg-red-100 text-red-700'
                : row.status === 'active' ? 'bg-green-100 text-green-700'
                : row.status === 'suspended' || row.status === 'cancelled' ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {row.status}
            </span>
          );
        case 7:
          return (
            <button
              onClick={() => {
                setRenewTarget(row);
                setRenewMonths(1);
                setRenewCycle('');
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary-200
                         text-primary-600 text-xs font-medium hover:bg-primary-50 whitespace-nowrap"
              title="Renew / extend this subscription"
            >
              <HiOutlineRefresh className="w-3.5 h-3.5" /> Renew
            </button>
          );
        default:
          return null;
      }
    }
    switch (colIndex) {
      case 0:
        return orgCell(row);
      case 1:
        return phoneCell(row);
      case 2:
        return ownerCell(row);
      case 3:
        return formatDate(row.dueDate);
      case 4:
        return row.status ? (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
            row.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {row.status.replace('_', ' ')}
          </span>
        ) : null;
      case 5:
        return formatDate(row.updatedAt || row.createdAt);
      case 6:
        return (
          <button
            onClick={() => {
              setRenewTarget(row);
              setRenewMonths(1);
              setRenewCycle('');
            }}
            disabled={!row.orgId}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary-200
                       text-primary-600 text-xs font-medium hover:bg-primary-50 whitespace-nowrap
                       disabled:opacity-40 disabled:cursor-not-allowed"
            title={row.orgId ? 'Renew / extend this subscription' : 'No organization linked to this task'}
          >
            <HiOutlineRefresh className="w-3.5 h-3.5" /> Renew
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineCash className="w-7 h-7 text-primary-500" />
            Renewals Overview
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Which subscriptions are due for renewal, and where each renewal stands
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                     hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          disabled={isFetching}
        >
          <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <ReportGuide
        id="renewal-report"
        summary="Use this page to make sure no customer’s subscription runs out without being renewed."
        points={[
          ["Upcoming", "Subscriptions ending soon that are not renewed yet."],
          ["Pending", "Renewal follow-up tasks your team has not finished."],
          ["Completed", "Renewal follow-up tasks your team has finished."],
          ["Missed", "Subscriptions whose end date passed without a renewal."],
        ]}
        tip="Click a box at the top to switch the list. Use “Renew” to extend a subscription."
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`bg-white rounded-xl border p-4 flex items-center gap-3 text-left transition-all ${
                activeTab === t.key
                  ? 'border-primary-300 ring-2 ring-primary-500/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${t.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{statValue(t.key)}</p>
                <p className="text-xs text-gray-500">{t.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active section table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 capitalize">{activeTab} Renewals</h2>
          <span className="text-xs text-gray-400">
            {report.windowDays && isOrgTab && `${activeTab === 'upcoming' ? 'Next' : 'Past'} ${report.windowDays} days · `}
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search organization, email, phone, owner…"
              className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            />
            {isOrgTab ? (
              <>
                <FilterSelect value={filters.plan} onChange={(v) => setFilters((f) => ({ ...f, plan: v }))}
                  allLabel="All Plans" options={uniqueSorted(allRows.map((r) => r.planName)).map((p) => [p, p])} />
                <FilterSelect value={filters.cycle} onChange={(v) => setFilters((f) => ({ ...f, cycle: v }))}
                  allLabel="All Billing" options={[['monthly', 'Monthly'], ['yearly', 'Yearly'], ['trial', 'Trial']]} />
                <FilterSelect value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                  allLabel="All Org Status" options={uniqueSorted(allRows.map((r) => r.status)).map((s) => [s, s])} />
              </>
            ) : (
              <FilterSelect value={filters.owner} onChange={(v) => setFilters((f) => ({ ...f, owner: v }))}
                allLabel="All Assignees" options={uniqueSorted(allRows.map((r) => r.ownerName || 'Unassigned')).map((o) => [o, o])} />
            )}
            <DateRangeFilter label={isOrgTab ? 'Expiry' : 'Due'} from={filters.from} to={filters.to}
              onChange={({ from, to }) => setFilters((f) => ({ ...f, from, to }))} />
            <ResetFiltersButton active={filterActive} onReset={() => setFilters(EMPTY_RENEWAL_FILTERS)} />
            <ExportMenu fileName={`renewals-${activeTab}`} columns={exportColumns} getRows={() => rows} />
          </div>
        )}

        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No renewals match these filters' : `No ${activeTab} renewal activities`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1050px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">S.No</th>
                  {columns.map((c) => (
                    <th key={c} className="px-4 py-3">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                    {columns.map((_, colIdx) => (
                      <td key={colIdx} className="px-4 py-3 text-sm text-gray-600">
                        {renderCell(row, colIdx)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Renew-subscription modal — renews from this report directly */}
      {renewTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Renew Subscription</h3>
              <button onClick={() => setRenewTarget(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{renewTarget.organizationName}</span>
                {' '}— currently ends {formatDate(renewTarget.dueDate)}
                {activeTab === 'missed' && (
                  <span className="text-red-600"> ({renewTarget.overdueDays} day{renewTarget.overdueDays !== 1 ? 's' : ''} overdue)</span>
                )}
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Extend By
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 3, 6, 12].map((m) => (
                    <button
                      key={m}
                      onClick={() => setRenewMonths(m)}
                      className={`px-2 py-2 rounded-xl border text-sm font-medium transition-colors ${
                        renewMonths === m
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {m === 12 ? '1 Yr' : `${m} Mo`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Added on top of the current expiry (or from today, if already expired)
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Billing Cycle (optional)
                </label>
                <select
                  value={renewCycle}
                  onChange={(e) => setRenewCycle(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                >
                  <option value="">Keep current cycle</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setRenewTarget(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRenew}
                disabled={renewMutation.isLoading}
                className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {renewMutation.isLoading ? 'Renewing…' : 'Renew Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewalPerformanceReport;